import { useEffect, useRef } from 'react';

const CursorEffectSection = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Get responsive scale factor based on viewport width
    const getScaleFactor = () => {
      const width = window.innerWidth;
      if (width <= 480) return 0.5; // Mobile
      if (width <= 768) return 0.7; // Tablet
      if (width <= 1024) return 0.85; // Small desktop
      return 1; // Full desktop
    };

    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
    };

    resizeCanvas(); // Initial resize

    const colors = ['#FF3366', '#FF6633', '#FFCC33', '#33CC66', '#3399FF', '#9966FF', '#FF99CC'];
    const playButtonColor = '#9966FF';
    const documentColors = [...colors];

    let playButtons = [];
    let documents = [];
    let mouseX = 0;
    let mouseY = 0;
    let sphereCounter = 0; // Add counter for more predictable generation

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;

      // Check if mouse is over a roadmap phase element
      const elementUnderMouse = document.elementFromPoint(e.clientX, e.clientY);
      const isOverRoadmapPhase = elementUnderMouse && (
        elementUnderMouse.closest('.roadmap-phase') || 
        elementUnderMouse.closest('.phase-content') ||
        elementUnderMouse.classList.contains('roadmap-phase') ||
        elementUnderMouse.classList.contains('phase-content')
      );

      // Only generate spheres if not over roadmap phases
      const scaleFactor = getScaleFactor();
      const threshold = scaleFactor < 0.7 ? 2 : 3; // Generate every 2-3 moves on mobile, 3 on desktop
      sphereCounter++;
      
      if (!isOverRoadmapPhase && sphereCounter >= threshold) {
        sphereCounter = 0; // Reset counter
        const baseSize = 35 * scaleFactor;
        const sizeVariation = 15 * scaleFactor;
        const maxButtons = scaleFactor < 0.7 ? 15 : 30; // Fewer particles on mobile
        
        playButtons.push({
          x: mouseX,
          y: mouseY,
          size: baseSize + Math.random() * sizeVariation,
          color: playButtonColor,
          opacity: 1,
          rotation: Math.random() * 20 - 10
        });
        if (playButtons.length > maxButtons) playButtons.shift();
      }
    };

    const handleClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;

      // Check if click is over a roadmap phase element
      const elementUnderMouse = document.elementFromPoint(e.clientX, e.clientY);
      const isOverRoadmapPhase = elementUnderMouse && (
        elementUnderMouse.closest('.roadmap-phase') || 
        elementUnderMouse.closest('.phase-content') ||
        elementUnderMouse.classList.contains('roadmap-phase') ||
        elementUnderMouse.classList.contains('phase-content')
      );

      // Only generate documents if not over roadmap phases
      if (!isOverRoadmapPhase) {
        const scaleFactor = getScaleFactor();
        const baseSize = 20 * scaleFactor;
        const sizeVariation = 30 * scaleFactor;
        const maxBaseSize = 100 * scaleFactor;
        const maxSizeVariation = 50 * scaleFactor;
        const distance = 100 * scaleFactor;
        const documentCount = scaleFactor < 0.7 ? 3 : 5; // Fewer documents on mobile
        
        for (let i = 0; i < documentCount; i++) {
          const angle = Math.random() * Math.PI * 2;
          const color = documentColors[Math.floor(Math.random() * documentColors.length)];
          documents.push({
            x: clickX + Math.cos(angle) * distance,
            y: clickY + Math.sin(angle) * distance,
            size: baseSize + Math.random() * sizeVariation,
            maxSize: maxBaseSize + Math.random() * maxSizeVariation,
            color,
            opacity: 1,
            rotation: Math.random() * 40 - 20
          });
        }
      }
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const rect = canvas.getBoundingClientRect();
      mouseX = touch.clientX - rect.left;
      mouseY = touch.clientY - rect.top;

      // Check if touch is over a roadmap phase element
      const elementUnderTouch = document.elementFromPoint(touch.clientX, touch.clientY);
      const isOverRoadmapPhase = elementUnderTouch && (
        elementUnderTouch.closest('.roadmap-phase') || 
        elementUnderTouch.closest('.phase-content') ||
        elementUnderTouch.classList.contains('roadmap-phase') ||
        elementUnderTouch.classList.contains('phase-content')
      );

      // Only generate spheres if not over roadmap phases
      const scaleFactor = getScaleFactor();
      const threshold = scaleFactor < 0.7 ? 4 : 5; // Less frequent on touch to avoid performance issues
      sphereCounter++;
      
      if (!isOverRoadmapPhase && sphereCounter >= threshold) {
        sphereCounter = 0; // Reset counter
        const baseSize = 35 * scaleFactor;
        const sizeVariation = 15 * scaleFactor;
        const maxButtons = scaleFactor < 0.7 ? 15 : 30;
        
        playButtons.push({
          x: mouseX,
          y: mouseY,
          size: baseSize + Math.random() * sizeVariation,
          color: playButtonColor,
          opacity: 1,
          rotation: Math.random() * 20 - 10
        });
        if (playButtons.length > maxButtons) playButtons.shift();
      }
    };

    const handleTouchStart = (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const rect = canvas.getBoundingClientRect();
      const touchX = touch.clientX - rect.left;
      const touchY = touch.clientY - rect.top;

      // Check if touch is over a roadmap phase element
      const elementUnderTouch = document.elementFromPoint(touch.clientX, touch.clientY);
      const isOverRoadmapPhase = elementUnderTouch && (
        elementUnderTouch.closest('.roadmap-phase') || 
        elementUnderTouch.closest('.phase-content') ||
        elementUnderTouch.classList.contains('roadmap-phase') ||
        elementUnderTouch.classList.contains('phase-content')
      );

      // Only generate documents if not over roadmap phases
      if (!isOverRoadmapPhase) {
        const scaleFactor = getScaleFactor();
        const baseSize = 20 * scaleFactor;
        const sizeVariation = 30 * scaleFactor;
        const maxBaseSize = 100 * scaleFactor;
        const maxSizeVariation = 50 * scaleFactor;
        const distance = 100 * scaleFactor;
        const documentCount = scaleFactor < 0.7 ? 2 : 3; // Even fewer on touch
        
        for (let i = 0; i < documentCount; i++) {
          const angle = Math.random() * Math.PI * 2;
          const color = documentColors[Math.floor(Math.random() * documentColors.length)];
          documents.push({
            x: touchX + Math.cos(angle) * distance,
            y: touchY + Math.sin(angle) * distance,
            size: baseSize + Math.random() * sizeVariation,
            maxSize: maxBaseSize + Math.random() * maxSizeVariation,
            color,
            opacity: 1,
            rotation: Math.random() * 40 - 20
          });
        }
      }
    };

    function animate() {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const scaleFactor = getScaleFactor();

      // Play buttons
      for (let i = playButtons.length - 1; i >= 0; i--) {
        const button = playButtons[i];
        button.opacity -= 0.02;
        if (button.opacity <= 0) {
          playButtons.splice(i, 1);
          continue;
        }

        ctx.save();
        ctx.translate(button.x, button.y);
        ctx.rotate(button.rotation * Math.PI / 180);

        // Add subtle glow effect with responsive blur
        ctx.shadowColor = '#71499C';
        ctx.shadowBlur = 20 * scaleFactor;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        // Purple sphere with gradient
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, button.size / 2);
        gradient.addColorStop(0, '#71499C' + Math.floor(button.opacity * 255).toString(16).padStart(2, '0'));
        gradient.addColorStop(0.7, '#5a3a7d' + Math.floor(button.opacity * 200).toString(16).padStart(2, '0'));
        gradient.addColorStop(1, '#3d2754' + Math.floor(button.opacity * 150).toString(16).padStart(2, '0'));
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(0, 0, button.size / 2, 0, Math.PI * 2);
        ctx.fill();

        // Add inner highlight for sphere effect
        ctx.shadowBlur = 0;
        const highlight = ctx.createRadialGradient(-button.size / 6, -button.size / 6, 0, -button.size / 6, -button.size / 6, button.size / 3);
        highlight.addColorStop(0, 'rgba(255,255,255,' + (button.opacity * 0.6) + ')');
        highlight.addColorStop(1, 'rgba(255,255,255,0)');
        
        ctx.fillStyle = highlight;
        ctx.beginPath();
        ctx.arc(0, 0, button.size / 2, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      }

      // Documents
      for (let i = documents.length - 1; i >= 0; i--) {
        const doc = documents[i];
        const growthRate = 3 * scaleFactor;
        
        doc.size += growthRate;
        doc.opacity = 1 - doc.size / doc.maxSize;
        if (doc.opacity <= 0 || doc.size >= doc.maxSize) {
          documents.splice(i, 1);
          continue;
        }

        ctx.save();
        ctx.translate(doc.x, doc.y);
        ctx.rotate(doc.rotation * Math.PI / 180);

        const lineWidth = Math.max(1, 2 * scaleFactor);
        const thinLineWidth = Math.max(0.5, 1 * scaleFactor);

        ctx.fillStyle = doc.color + Math.floor(doc.opacity * 255).toString(16).padStart(2, '0');
        ctx.strokeStyle = 'rgba(255,255,255,' + doc.opacity + ')';
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        ctx.rect(-doc.size / 2, -doc.size / 2, doc.size * 0.8, doc.size);
        ctx.fill();
        ctx.stroke();

        ctx.strokeStyle = 'rgba(255,255,255,' + doc.opacity * 0.5 + ')';
        ctx.lineWidth = thinLineWidth;
        [-doc.size / 4, 0, doc.size / 4].forEach(y => {
          ctx.beginPath();
          ctx.moveTo(-doc.size / 3, y);
          ctx.lineTo(doc.size / 4, y);
          ctx.stroke();
        });

        const foldSize = doc.size * 0.2;
        ctx.beginPath();
        ctx.moveTo(doc.size * 0.3 - foldSize, -doc.size / 2);
        ctx.lineTo(doc.size * 0.3, -doc.size / 2 + foldSize);
        ctx.lineTo(doc.size * 0.3, -doc.size / 2);
        ctx.closePath();
        const tempColor = doc.color.replace('#', '');
        const r = parseInt(tempColor.substr(0, 2), 16);
        const g = parseInt(tempColor.substr(2, 2), 16);
        const b = parseInt(tempColor.substr(4, 2), 16);
        ctx.fillStyle = `rgba(${Math.min(r + 40, 255)}, ${Math.min(g + 40, 255)}, ${Math.min(b + 40, 255)}, ${doc.opacity})`;
        ctx.fill();
        ctx.stroke();

        ctx.restore();
      }

      requestAnimationFrame(animate);
    }

    const handleResize = () => {
      resizeCanvas();
      // Clear effects when resizing to prevent visual glitches
      playButtons.length = 0;
      documents.length = 0;
      sphereCounter = 0; // Reset counter on resize
    };

    animate();

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);
    window.addEventListener('resize', handleResize);
    
    // Touch events for mobile (on canvas for better performance)
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('resize', handleResize);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchstart', handleTouchStart);
    };
  }, []);

  return (
    <
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 0
        }}
      />
     
    </>
  );
};

export default CursorEffectSection;
